/*
 * Copyright (c) 2025 Rembrand Pardo
 * Unauthorized copying, modification, or redistribution is strictly prohibited.
 * Violators will face legal action.
 */

//../components/AboutPage.js
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import '../styles/AboutPage.css';
import '../styles/GradientBackground.css';
import { logEvent } from 'firebase/analytics';
import { analytics } from '../firebaseConfig';

import { TextHoverEffect } from "../components/TextHoverEffect";
import { twMerge } from "tailwind-merge";
import { TracingBeam } from "../components/TracingBeam";


const AboutPage = ({ translations, language })  => {

  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    logEvent(analytics, "page_view", { page: "About Page" });
    const timer = setTimeout(() => {
      setIsVisible(true); // Set visible to true after delay
    }, 140); // Adjust delay as needed

    return () => clearTimeout(timer); // Cleanup on unmount
  }, []);

  const logUserInteraction = (eventName) => {
    logEvent(analytics, "user_activity", { event: eventName });
    console.log(`User activity logged: ${eventName}`);
  };

  const aboutContent = [
    {
      title: translations.pardoFamilyTitle,
      description: translations.pardoFamilyDescription,
      badge: translations.pardoFamilyMottoBadge, 
      image:
      language === "es"
        ? "/crest_es.png"
        : language === "ca"
        ? "/crest_ca.png"
        : "/pardo_crest_no_motto.jpg",
    },
    {
      title: translations.calculatedRisksTitle,
      description: translations.calculatedRisksDescription,
      badge: translations.calculatedRisksBadge,
      image: "/adventures.jpg",
    },
    {
      title: translations.steppingIntoRolesTitle,
      description: translations.steppingIntoRolesDescription,
      badge: translations.steppingIntoRolesBadge,
      image: "/acting.jpg",
    },
    {
      title: translations.endlessLearningTitle,
      description: translations.endlessLearningDescription,
      badge: translations.endlessLearningBadge, 
      image: "/books.png",
    },
  ];


  return (
    <div className={`about_page ${isVisible ? 'visible' : ''}`}>

      <Helmet>
        <title>About - Rembrand Pardo</title>
        <meta name="description" content="Learn more about Rembrand Pardo, his background, passions, and journey in business and technology." />
        <meta name="keywords" content="Rembrand Pardo, about Rembrand, about Pardo, business strategy, leadership, learning, technology, passions, business solution, congress" />
      
      {/* Structured Data (JSON-LD) for Services */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "name": "About - Rembrand Pardo",
            "url": "https://www.rembrandpardo.com/about",
            "mainEntity": {
              "@type": "Person",
              "name": "Rembrand Pardo",
              "description": "A business strategist and consultant with expertise in management, leadership, and technology-driven solutions.",
              "alumniOf": [
                {
                  "@type": "EducationalOrganization",
                  "name": "Brigham Young University",
                  "description": "A private prestigious research university located in Provo, Utah, USA.",
                  "degreeAwarded": [
                    {
                      "@type": "EducationalOccupationalCredential",
                      "name": "Bachelor of Science in Computer Science with an emphasis in Software Development and Management"
                    },
                    {
                      "@type": "EducationalOccupationalCredential",
                      "name": "Minor in Mathematics"
                    }
                  ]
                },
                {
                  "@type": "EducationalOrganization",
                  "name": "Salt Lake Community College",
                  "description": "A community college in Salt Lake City, Utah, USA.",
                  "degreeAwarded": {
                    "@type": "EducationalOccupationalCredential",
                    "name": "Associate of Science in Business and Management"
                  }
                }
              ]
            }
          })}
        </script>
      </Helmet>

      <div className="background-gradient" /> {/* gradient background is for the whole page */}
    
      <div className="about_page_content">

        <section className='about_heading_section'>
          <h1>{translations.about_heading}</h1>
        </section>

        <section className='body_section'>
          <p>{translations.aboutMeText1}</p>
          <p>{translations.aboutMeText2}</p>
        </section>

        <section className='secondHeading_section' onMouseEnter={() => logUserInteraction("Hovered over My Passions")}>
          {/* Make the height and flexbox properties responsive */}
          <div className="h-[10rem] sm:h-[8rem] xs:h-[6rem] flex items-center justify-center">
            <TextHoverEffect text={translations.myPassions} />
          </div>
        </section>



        <section className='tracingBeam_section'>
          
          <TracingBeam className="px-6">
            <div className="max-w-2xl mx-auto antialiased pt-4 relative">
              {aboutContent.map((item, index) => (
                <div key={`content-${index}`} className="mb-10">
                  <h2 className="bg-black text-white rounded-full text-sm w-fit px-4 py-1 mb-4">
                    {item.badge}
                  </h2>
      
                  <p className={twMerge("mb-4")}>
                    {item.title}
                  </p>

                  <div className="text-sm  prose prose-sm dark:prose-invert">
                    {item?.image && (
                      <img
                        src={item.image}
                        alt="blog thumbnail"
                        height="1000"
                        width="1000"
                        className="rounded-lg mb-10 object-cover"
                      />
                    )}
                    {item.description}
                  </div>
                </div>
              ))}
            </div>
          </TracingBeam>

        </section>

        <section className='about_end_page' onMouseEnter={() => logUserInteraction("Scrolled to end of About Page")} >

          {translations.aboutEndPage}

        </section>

      </div>

      {/* Hidden SEO text moved to the very bottom of the page */}
      <div className="seo-text" aria-hidden="true">
      I am driven by the synergy of people, business, and technology—three essential pillars that inspire my purpose and pursuit of impactful solutions. My journey is fueled by relentless curiosity, a commitment to excellence, and a dedication to empowering others through innovative strategies and meaningful collaboration. Fluent in English, Spanish, and Catalan, I bring a global perspective to every challenge and project.
      Beyond my professional pursuits, I’m an avid learner and a lover of sports, music, movies, and acting. These passions keep me grounded while inspiring the creativity and focus I bring to my work. Dive deeper into this page to uncover the passions, values, and aspirations that define me and guide my mission to create impactful solutions.
      </div>
      
    </div>
  );
};

export default AboutPage;
